import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import "./ExpenseGraphDashboard.css";

const COMPANY_MAP = {
  svb: "SVB",
  sdc: "SDC",
  magnira: "Magnira Universe",
};

const MONTHS = [
  "All",
  "Apr-26",
  "May-26",
  "Jun-26",
  "Jul-26",
  "Aug-26",
  "Sep-26",
  "Oct-26",
  "Nov-26",
  "Dec-26",
  "Jan-27",
  "Feb-27",
  "Mar-27",
];

const ExpenseGraphDashboard = ({ companyKey = "svb" }) => {
  const company = COMPANY_MAP[companyKey] || "SVB";

  const [graphData, setGraphData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [financialYears, setFinancialYears] = useState(["2026-27"]);
  const [financialYear, setFinancialYear] = useState("2026-27");
  const [month, setMonth] = useState("All");
  const [loading, setLoading] = useState(false);

  const title = useMemo(() => {
    if (company === "Magnira Universe") return "Magnira Expenses";
    return `${company} Expenses`;
  }, [company]);

  const formatINR = (value) =>
    `₹ ${Number(value || 0).toLocaleString("en-IN")}`;
  const totalGraphAmount = graphData.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0,
  );

  const graphDataWithPercent = graphData.map((item) => ({
    ...item,
    percentage:
      totalGraphAmount > 0
        ? ((item.amount / totalGraphAmount) * 100).toFixed(1)
        : 0,
  }));
  const highestExpenseHead = useMemo(() => {
    if (!graphData.length) return null;

    return [...graphData].sort(
      (a, b) => Number(b.amount) - Number(a.amount),
    )[0];
  }, [graphData]);
  const fetchFilters = async () => {
    try {
      const res = await api.get("/expense-graph/filters");

      const years = res.data?.financialYears?.length
        ? res.data.financialYears
        : ["2026-27"];

      setFinancialYears(years);

      if (!years.includes(financialYear)) {
        setFinancialYear(years[0]);
      }
    } catch (error) {
      console.error("Filter fetch error:", error);
    }
  };

  const fetchGraphData = async () => {
    try {
      setLoading(true);

      const res = await api.get("/expense-graph/graph", {
        params: { company, financialYear, month },
      });

      setGraphData(res.data?.data || []);
    } catch (error) {
      console.error("Graph fetch error:", error);
      setGraphData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await api.get("/expense-graph/monthly-summary", {
        params: { company, financialYear },
      });

      setSummary(res.data);
    } catch (error) {
      console.error("Summary fetch error:", error);
      setSummary(null);
    }
  };

  const syncGoogleSheet = async () => {
    try {
      setLoading(true);

      await api.post("/expense-graph/sync-google-sheet", {
        company,
      });

      await fetchFilters();
      await fetchGraphData();
      await fetchSummary();

      alert(`${company} Google Sheet synced successfully`);
    } catch (error) {
      console.error("Sync error:", error);
      alert(error?.response?.data?.message || "Google Sheet sync failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchGraphData();
    fetchSummary();
  }, [company, financialYear, month]);

  return (
    <div className="expense-page">
      <div className="expense-header">
        <div>
          <h2>{title}</h2>
        </div>

        <button
          type="button"
          onClick={syncGoogleSheet}
          disabled={loading}
          className="sync-btn"
        >
          {loading ? "Loading..." : "Sync Google Sheet"}
        </button>
      </div>

      <div className="filter-card">
        <div className="field">
          <label>Company</label>
          <input value={company} readOnly />
        </div>

        <div className="field">
          <label>Financial Year</label>
          <select
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
          >
            {financialYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Month</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            {MONTHS.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
      </div>

      {summary && (
        <div className="summary-grid">
          <div className="summary-card">
            <p>Total Expense</p>
            <h2>{formatINR(summary.totalExpense)}</h2>
          </div>

          <div className="summary-card">
            <p>Highest Month</p>
            <h2>{summary.highestMonth?.month || "-"}</h2>
            <span>{formatINR(summary.highestMonth?.amount)}</span>
          </div>

          <div className="summary-card">
            <p>Lowest Month</p>
            <h2>{summary.lowestMonth?.month || "-"}</h2>
            <span>{formatINR(summary.lowestMonth?.amount)}</span>
          </div>

          <div className="summary-card">
            <p>Average Monthly</p>
            <h2>{formatINR(summary.averageExpense)}</h2>
          </div>
        </div>
      )}

      <div className="chart-card">
        <div className="chart-header">
          <h3>
            {month === "All"
              ? `Expense Head Analysis - ${financialYear}`
              : `Expense Head Analysis - ${month}`}
          </h3>

          <span>{graphData.length} items</span>
        </div>

        {loading ? (
          <div className="empty-box">Loading graph...</div>
        ) : graphData.length === 0 ? (
          <div className="empty-box">
            No graph data found. Pehle Sync Google Sheet button click karo.
          </div>
        ) : (
          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={graphDataWithPercent}
                margin={{ top: 20, right: 30, left: 0, bottom: 90 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="expenseHead"
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                  height={150}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip
                  formatter={(value, name, props) => [
                    `${formatINR(value)} (${props.payload.percentage}%)`,
                    "Expense",
                  ]}
                />
                <Bar dataKey="amount" fill="#5b9bd5" radius={[6, 6, 0, 0]}>
                  <LabelList
                    dataKey="percentage"
                    position="top"
                    formatter={(value) => `${value}%`}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {summary?.monthlyData?.length > 0 && (
        <div className="chart-card">
          <div className="chart-header">
            <h3>Monthly Expense Trend - {financialYear}</h3>
          </div>

          <div className="monthly-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={summary.monthlyData}
                margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatINR(value)} />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#2563eb"
                  strokeWidth={3}
                  dot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* {summary && (
        <div className="report-card">
          <h3>Expense Analysis Report</h3>

          <p>
            In <b>{financialYear}</b>, total expense for <b>{company}</b> is{" "}
            <b>{formatINR(summary.totalExpense)}</b>.
          </p>

          <p>
            Highest expense happened in <b>{summary.highestMonth?.month}</b>{" "}
            with <b>{formatINR(summary.highestMonth?.amount)}</b>.
          </p>

          <p>
            Lowest expense happened in <b>{summary.lowestMonth?.month}</b> with{" "}
            <b>{formatINR(summary.lowestMonth?.amount)}</b>.
          </p>

          <p>
            Average monthly expense is{" "}
            <b>{formatINR(summary.averageExpense)}</b>.
          </p>
          {highestExpenseHead && (
            <p>
              Highest expense head is <b>{highestExpenseHead.expenseHead}</b>{" "}
              with <b>{formatINR(highestExpenseHead.amount)}</b>.
            </p>
          )}
        </div>
      )} */}
    </div>
  );
};

export default ExpenseGraphDashboard;
